import { $isLinkNode } from '@lexical/link';
import { $isListItemNode, $isListNode, ListNode } from '@lexical/list';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import { $createQuoteNode } from '@lexical/rich-text';
import { $isAtNodeEnd } from '@lexical/selection';
import { $wrapNodes } from '@lexical/selection';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import { createCommand } from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CustomParagraphNode } from '~components/generic/editable-field/components/nodes/CustomParagraphNode';
import { handleStyleCommand } from '~components/generic/editable-field/utils/handleStyleCommand';
import { BlockOptionsList } from '../components/BlockOptionsList';
import {
  JustifyIcon,
  LinkIconL,
  ListOlIcon,
  ListUlIcon,
  TextCenterIcon,
  TextLeftIcon,
  TextRightIcon,
  TypeBoldIcon,
  TypeItalicIcon,
  TypeStrikethroughIcon,
} from '../icons';

const APPLY_XL_STYLE_COMMAND = createCommand('APPLY_XL_STYLE_COMMAND');
const APPLY_SECONDARY_COLOR_COMMAND = createCommand('APPLY_SECONDARY_COLOR_COMMAND');

const LowPriority = 1;

function getSelectedNode(selection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

interface ToolbarPluginProps {
  includedControls?: string[];
  onOpenTextLinkWidget: () => void;
}

export default function ToolbarPlugin({ onOpenTextLinkWidget, includedControls }: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] = useState('paragraph');
  const [showBlockOptionsList, setShowBlockOptionsList] = useState(false);
  const [defaultToolbarList, setDefaultToolbarList] = useState(true);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isXL, setIsXL] = useState(false);
  const [isX2, setIsX2] = useState(false);
  const [isSecondaryColor, setIsSecondaryColor] = useState(false);
  const [isLeftAlign, setIsLeftAlign] = useState(false);
  const [isCenterAlign, setIsCenterAlign] = useState(false);
  const [isRightAlign, setIsRightAlign] = useState(false);
  const [isJustifyAlign, setIsJustifyAlign] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          if (element instanceof CustomParagraphNode) {
            setBlockType(element.__customTagName);
          } else {
            const type = $isHeadingNode(element) ? element.getTag() : element.getType();
            setBlockType(type);
          }
        }
      }
      // Check for element format (alignment)

      // Use the format property instead of getFormatType()
      const format = element.getFormat();
      const formatType = format & 0b111; // Extract alignment bits

      setIsLeftAlign(formatType === 1);
      setIsCenterAlign(formatType === 2);
      setIsRightAlign(formatType === 3);
      setIsJustifyAlign(formatType === 4);

      // Update text format
      setIsX2(selection.hasFormat('superscript'));
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));

      // Update links
      const node = getSelectedNode(selection);
      if (node) {
        const parent = node.getParent();
        if (parent && parent.getType() === 'custom-paragraph') {
          setIsXL(parent.__customClassName?.includes('u-size-xl') || false);
          setIsSecondaryColor(parent.__customClassName?.includes('color--brand-second') || false);
        } else {
          setIsXL(false);
          setIsSecondaryColor(false);
        }
        if ($isLinkNode(parent) || $isLinkNode(node)) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      }
    }
  }, [editor]);
  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        _payload => {
          updateToolbar();
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        payload => {
          setCanUndo(payload);
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        payload => {
          setCanRedo(payload);
          return false;
        },
        LowPriority,
      ),
    );
  }, [editor, updateToolbar]);

  const insertLink = useCallback(() => {
    onOpenTextLinkWidget();
    setDefaultToolbarList(false);
  }, [onOpenTextLinkWidget]);

  const formatBulletList = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (blockType !== 'ul') {
        // Converting to a list - first clear any alignment
        const hasAlignment = isLeftAlign || isCenterAlign || isRightAlign || isJustifyAlign;

        if (hasAlignment) {
          // Clear alignment first, then create list
          setTimeout(() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left'); // Reset to default

            setTimeout(() => {
              editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
            }, 10);
          }, 0);
        } else {
          // No alignment to clear, just create list
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        }
      } else {
        // Converting from list to paragraphs
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      }
    });
  };

  const formatNumberedList = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (blockType !== 'ol') {
        // Converting to a list - first clear any alignment
        const hasAlignment = isLeftAlign || isCenterAlign || isRightAlign || isJustifyAlign;

        if (hasAlignment) {
          // Clear alignment first, then create list
          setTimeout(() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left'); // Reset to default

            setTimeout(() => {
              editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
            }, 10);
          }, 0);
        } else {
          // No alignment to clear, just create list
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
        }
      } else {
        // Converting from list to paragraphs
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      }
    });
  };
  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createQuoteNode());
        }
      });
    }
  };

  const applyTextAlignment = alignmentType => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const nodes = selection.getNodes();

      // Check if any selected node is in a list
      let hasListItem = false;
      nodes.forEach(node => {
        let parent = node.getParent();
        while (parent) {
          if ($isListItemNode(parent) || $isListNode(parent)) {
            hasListItem = true;
            break;
          }
          parent = parent.getParent();
        }
      });

      // Get the current element to check its alignment
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();

      // Determine if we need to toggle off (element already has the requested alignment)
      const format = element.getFormat();
      const formatType = format & 0b111; // Extract alignment bits

      // Map alignmentType string to format number
      const alignmentMap = { left: 1, center: 2, right: 3, justify: 4 };
      const currentAlignment = alignmentMap[alignmentType];

      // If current alignment matches what user clicked, toggle to default (left)
      const targetAlignment = formatType === currentAlignment ? 'left' : alignmentType;

      if (hasListItem) {
        // If in a list, we need to remove the list first
        // We'll queue this as a separate update to ensure proper execution order
        setTimeout(() => {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);

          // After removing the list, apply the alignment in another update
          setTimeout(() => {
            editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, targetAlignment);
          }, 10);
        }, 0);
      } else {
        // Not in a list, just apply the alignment directly
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, targetAlignment);
      }
    });
  };

  // register custom commands for secondary color, XL
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        APPLY_SECONDARY_COLOR_COMMAND,
        () => handleStyleCommand(editor, 'color--brand-second'),
        LowPriority,
      ),
      editor.registerCommand(APPLY_XL_STYLE_COMMAND, () => handleStyleCommand(editor, 'u-size-xl'), LowPriority),
    );
  }, [editor]);

  return (
    <div className="toolbar" ref={toolbarRef}>
      <>
        {showBlockOptionsList && (
          <BlockOptionsList
            editor={editor}
            blockType={blockType}
            toolbarRef={toolbarRef}
            setShowBlockOptions={() => {
              setShowBlockOptionsList(false);
              setDefaultToolbarList(true);
            }}
            includedControls={includedControls}
          />
        )}

        {defaultToolbarList && (
          <>
            {includedControls.includes('undo') && (
              <button
                disabled={!canUndo}
                onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
                className="toolbar-item spaced"
                aria-label="Undo"
              >
                <i className="format undo" />
              </button>
            )}

            {includedControls.includes('redo') && (
              <button
                disabled={!canRedo}
                onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
                className="toolbar-item"
                aria-label="Redo"
              >
                <i className="format redo" />
              </button>
            )}

            <>
              {includedControls.includes('bold') && (
                <button
                  onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
                  className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
                  aria-label="Format Bold"
                >
                  <TypeBoldIcon />
                </button>
              )}
              {includedControls.includes('italic') && (
                <button
                  onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
                  className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
                  aria-label="Format Italics"
                >
                  <TypeItalicIcon />
                </button>
              )}
              {(includedControls.includes('headlineOne') ||
                includedControls.includes('headlineTwo') ||
                includedControls.includes('headlineThree') ||
                includedControls.includes('headlineFour') ||
                includedControls.includes('headlineFive') ||
                includedControls.includes('headlineSix')) && (
                <button
                  className={`toolbar-item block-controls ${blockType.startsWith('h') ? 'active' : ''}`}
                  onClick={() => {
                    setShowBlockOptionsList(!showBlockOptionsList);
                    setDefaultToolbarList(false);
                  }}
                  aria-label="Formatting Options"
                >
                  <span className={'toolbar-item format block-type-text ' + (showBlockOptionsList ? 'active' : '')}>
                    H
                  </span>
                </button>
              )}
              {includedControls.includes('underline') && (
                <button
                  onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
                  className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
                  aria-label="Format Underline"
                >
                  <i className="format underline" />
                </button>
              )}
              {includedControls.includes('strikethrough') && (
                <button
                  onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
                  className={'toolbar-item spaced ' + (isStrikethrough ? 'active' : '')}
                  aria-label="Format Strikethrough"
                >
                  <TypeStrikethroughIcon />
                </button>
              )}
              {includedControls.includes('orderedList') && (
                <button
                  className={'toolbar-item spaced ' + (blockType === 'ol' ? 'active' : '')}
                  onClick={formatNumberedList}
                  aria-label="Numbered List"
                >
                  <ListOlIcon />
                </button>
              )}
              {includedControls.includes('unorderedList') && (
                <button
                  className={'toolbar-item spaced ' + (blockType === 'ul' ? 'active' : '')}
                  onClick={formatBulletList}
                  aria-label="Bullet List"
                >
                  <ListUlIcon />
                </button>
              )}
              {includedControls.includes('alignTextLeft') && (
                <button
                  onClick={() => applyTextAlignment('left')}
                  className={'toolbar-item spaced ' + (isLeftAlign ? 'active' : '')}
                  aria-label="Left Align"
                >
                  <TextLeftIcon />
                </button>
              )}
              {includedControls.includes('alignTextCenter') && (
                <button
                  onClick={() => applyTextAlignment('center')}
                  className={'toolbar-item spaced ' + (isCenterAlign ? 'active' : '')}
                  aria-label="Center Align"
                >
                  <TextCenterIcon />
                </button>
              )}
              {includedControls.includes('alignTextRight') && (
                <button
                  onClick={() => applyTextAlignment('right')}
                  className={'toolbar-item spaced ' + (isRightAlign ? 'active' : '')}
                  aria-label="Right Align"
                >
                  <TextRightIcon />
                </button>
              )}
              {includedControls.includes('link') && (
                <button
                  onClick={insertLink}
                  className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
                  aria-label="Insert Link"
                >
                  <LinkIconL />
                </button>
              )}{' '}
              {includedControls.includes('secondaryColor') && (
                <button
                  onClick={() => editor.dispatchCommand(APPLY_SECONDARY_COLOR_COMMAND, undefined)}
                  className={'toolbar-item spaced ' + (isSecondaryColor ? 'active' : '')}
                  aria-label="Apply Secondary Color Style"
                >
                  Secondary Color
                </button>
              )}
              {includedControls.includes('sup') && (
                <button
                  onClick={() => {
                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
                  }}
                  className={'toolbar-item spaced ' + (isX2 ? 'active' : '')}
                  aria-label="Format text with a superscript"
                >
                  x2
                </button>
              )}
              {includedControls.includes('xL') && (
                <button
                  onClick={() => editor.dispatchCommand(APPLY_XL_STYLE_COMMAND, undefined)}
                  className={'toolbar-item spaced ' + (isXL ? 'active' : '')}
                  aria-label="Apply XL Style"
                >
                  XL
                </button>
              )}
              {includedControls.includes('alignTextJustify') && (
                <button
                  onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')}
                  className={'toolbar-item ' + (isJustifyAlign ? 'active' : '')}
                  aria-label="Justify Align"
                >
                  <JustifyIcon />
                </button>
              )}
              {includedControls.includes('quote') && (
                <button className="toolbar-item" onClick={formatQuote}>
                  {blockType === 'quote' && <span className="active" />}
                </button>
              )}
            </>
          </>
        )}
      </>
    </div>
  );
}
